import { AppDispatch, RootState } from '../index';
import {
  mergeHydratedSnippets,
  toggleCompanyExpanded,
  setAllExpanded,
  setCompaniesExpanding,
} from './slice';
import { postHydrateEmbeddings } from '../../api/signalSearchApi';

// Helper: hydrate a batch of embedding IDs and merge into store
const hydrateAndMerge = async (dispatch: AppDispatch, embeddingIds: string[]) => {
  if (!embeddingIds.length) return;
  const hydrated = await postHydrateEmbeddings({ embeddingIds });
  dispatch(mergeHydratedSnippets(Object.fromEntries(hydrated.map((h) => [h.embeddingId, h]))));
};

// Toggle a company's expanded state. On expand, hydrate the first snippet per document for that company.
export const toggleCompanyExpandedAndHydrate =
  (companyId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().signalSearch;
    const isExpanding = !state.expandedCompanies[companyId];
    dispatch(toggleCompanyExpanded({ companyId }));
    if (!isExpanding) return;
    dispatch(setCompaniesExpanding({ companyIds: [companyId], expanding: true }));

    const company = state.companies.find((c) => c.companyId === companyId);
    if (!company) return;
    const firstIds = company.documents
      .map((d) => d.embeddings[0]?.embeddingId)
      .filter((id): id is string => Boolean(id) && !state.hydratedById[id]);
    try {
      await hydrateAndMerge(dispatch, firstIds);
    } finally {
      dispatch(setCompaniesExpanding({ companyIds: [companyId], expanding: false }));
    }
  };

// Expand all companies and hydrate one snippet per document across all companies in a single request.
export const expandAllAndHydrate =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().signalSearch;
    const allCompanyIds = state.companies.map((c) => c.companyId);
    dispatch(setCompaniesExpanding({ companyIds: allCompanyIds, expanding: true }));
    dispatch(setAllExpanded({ expanded: true }));
    const allIds = state.companies
      .flatMap((c) => c.documents.map((d) => d.embeddings[0]?.embeddingId))
      .filter((id): id is string => Boolean(id) && !state.hydratedById[id]);
    try {
      await hydrateAndMerge(dispatch, allIds);
    } finally {
      dispatch(setCompaniesExpanding({ companyIds: allCompanyIds, expanding: false }));
    }
  };
